/**
 * JoinPage — the PUBLIC, token-gated registration page a guest lands on after
 * scanning a staff-generated QR (/join?token=...). It validates the token, shows
 * the right household form (member vs non-member, per the token's kind), submits
 * to /registration/join, and logs the new primary in.
 *
 * Public route — NOT wrapped in ProtectedRoute. Uses registrationService's bare
 * client so an expired-code 401 renders inline instead of bouncing to /login.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Container, Paper, Typography, Alert, CircularProgress, Chip, Stack } from '@mui/material';
import PoolIcon from '@mui/icons-material/Pool';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { loginSuccess } from '../store/slices/authSlice';
import registrationService, { RegistrationKind } from '../api/services/registrationService';
import { getErrorMessage } from '../api/axiosConfig';
import HouseholdForm from '../components/HouseholdForm';

const JoinPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [checking, setChecking] = useState(true);
  const [kind, setKind] = useState<RegistrationKind | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!token) { setChecking(false); setTokenError('missing'); return; }
    registrationService.validateToken(token)
      .then((res) => { if (active) { setKind(res.kind); setChecking(false); } })
      .catch((err) => { if (active) { setTokenError(getErrorMessage(err)); setChecking(false); } });
    return () => { active = false; };
  }, [token]);

  const handleSubmit = async (household: any) => {
    setSubmitting(true);
    setServerError(null);
    try {
      const res = await registrationService.join(token, household);
      if (res.access_token) {
        dispatch(loginSuccess({ token: res.access_token, user: res.data?.primary }));
        navigate('/dashboard');
      } else {
        navigate('/login');
      }
    } catch (err) {
      setServerError(getErrorMessage(err));
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: { xs: 3, sm: 6 }, mb: 4 }}>
        <Paper elevation={3} sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <PoolIcon color="primary" />
            <Typography variant="h5" component="h1" fontWeight={700}>Join Fig Swim</Typography>
          </Stack>

          {checking && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          )}

          {!checking && tokenError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {tokenError === 'missing'
                ? 'Open the sign-up QR code a staff member shows you to register.'
                : 'This sign-up code is invalid or has expired. Please ask a staff member to show you a fresh QR code.'}
            </Alert>
          )}

          {!checking && !tokenError && kind && (
            <>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Registering as a {kind === 'member' ? 'member' : 'guest (non-member)'}. Add your whole household below.
                </Typography>
                <Chip size="small" color={kind === 'member' ? 'success' : 'warning'}
                  label={kind === 'member' ? 'Member' : 'Non-member'} variant="outlined" />
              </Stack>
              <HouseholdForm
                type={kind === 'member' ? 'member' : 'non-member'}
                submitting={submitting}
                serverError={serverError}
                onSubmit={handleSubmit}
              />
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default JoinPage;
